/** @jsx tsx */
import { DNode } from "@dojo/framework/core/interfaces";
import { tsx } from "@dojo/framework/core/vdom";
import { ADDITIONAL_DIMENSION_FACTOR, ANGLE_ZERO, TEMP_SHAPE } from "../../Constants";
import { IGeoBoundaryMeta } from "../interfaces/model/dataStructures/GeoBoundaryMeta";
import { CanvasMode, IPageObjectTypes } from "../interfaces/model/enumerations/index";
import { ITextStyles } from "../interfaces/model/index";
import { IAbstractPageObject } from "../interfaces/model/PageObjects/AbstractPageObject";
import { IShape } from "../interfaces/model/PageObjects/Shape";
import { IPageObjectProperties } from "../interfaces/model/properties/pageObjectProperties";
import { ITransform2D } from "../interfaces/model/properties/Transform2D";
import { allowMultiShapeSelection, cancelCurrentOperation } from "../keyUtil";
import { IDragResults, IPosition } from "../meta/Drag/index";
import { stateConstants } from "../selectors/index";
import { getMaxDimension } from "../util";
import { DraggableWidget } from "./DraggableWidget";
import { IPicture } from "../interfaces/model/PageObjects";
import { getPanningMode } from "canvas/store/PageHelper";
import { generateUUID } from "canvas/store/reducers/util";


/**
 *  Page object render properties
 */
export interface IPageObjectRenderProps<T extends
	IAbstractPageObject<IPageObjectTypes, IPageObjectProperties<ITransform2D>>> {

	/** objects to highlight */
	highlightType?: string;

	/**
	 * Page object model. Either shape or picture or group shape state model
	 */
	pageObject: T;

	/**
	 * If true, it indicates if this widget is participating in group; otherwise false.
	 */
	partOfGroup: boolean;

	/** depth of the page object  */
	depth: number;

	/** Page id of the selected page */
	selectedPageId?: string;

	/** Whether the object is part of a locked group */
	absoluteLock? : boolean;

	/**
	 * Is page object selected.
	 */
	isSelected?: boolean;

	/** Canvas container html element, if one is not set, it will default to document body */
	canvasContainerEl: HTMLElement;

	/**
	 * Current canvas mode.
	 */
	canvasMode: CanvasMode;

	/**
	 * Default Text Styles for shape
	 */
	defaultTextStyles?: ITextStyles;

	/**
	 * Optional transform origin position. When available, the transformation will happen around this point.
	 * This is introduced to get ghost image for cropping tool positioned correctly with respect to shape.
	 * If omitted, "center" is used as transform origin
	 */
	transformOrigin?: IPosition;

	/** Shape render-able geo boundary meta */
	geoBoundaryMeta?: IGeoBoundaryMeta;

	/** If part of a group, whether the parent group is selected */
	parentGroupIsSelected?: boolean;

	/**
	 * Select object callback
	 * @param selectedObject Object index in its parent child list
	 * @param isMultiSelect Flag to indicate multi selection
	 */
	selectObject(selectedObject: number, isMultiSelect?: boolean, transient?: boolean, childId?: number): void;

	/**
	 * Select object by id callback
	 * @param selecionId {String} id of object to be selected.
	 * @param isMultiSelect {Boolean} if true, multiple objects to be selected
	 * @param trans {Boolean} transient
	 * @param forceSingleSelect {Boolean} if true, force all other objects to deselect
	 * @returns {BatchAction} Select object action.
	*/
	selectObjectById(selectionId: string, isMultiSelect: boolean, trans: boolean, forceSingleSelect?: boolean) : void;

	/**
	 * Apply format painter callback
	 * @param targetObjectIndex Target object index
	 */
	applyFormatPainter?(targetShapeId: string): void;

	/** Callback to set page hightlight to primary  */
	addPageDragOverClass(): void;

	/**
	 * Adjust highlight callback
	 */
	adjustHighlight?(highlightType: Set<string>): void;

	/**
	 * Translate object callback
	 * @param delta Drag event delta
	 * @param transient undo/redo stack flag.
	 */
	translateObjects?(delta: IDragResults, transient?: boolean, isEscape?: boolean): void;

	/**
	 * Reset guides callback
	 */
	resetGuides?(): void;
	/**
	 * Edit object callback.
	 */
	editPageObject?(custom?: boolean, exitEdit?: boolean, shapeId?: string): void;

	/**
	 * mouse enter event, if it doesn't use the default
	 */
	onMouseEnter?(event: MouseEvent): void;

	/**
	 * mouse leave event, if it doesn't use the default
	 */
	onMouseLeave?(event: MouseEvent): void;

	/** Callback to set page hightlight to grey */
	removePageDragOverClass(): void;

	/** Set text style action callback */
	setTextStyle?(style: string, value: string, transient?: boolean): void;

	/** Set selected shape extends */
	setObjectExtends(extend: {cx?: number; cy?: number}, transient?: boolean): void;

	/** Deselect all objects callback function */
	deselectAllObjects?(): void;

	/** Set operation in progress callback function */
	setOperationInProgress?(progress: boolean): void;

	/** Remove migrated extents callback function */
	removeMigratedExtents?(shapeIds: string[]): void;
}

/**
 * Abstract Page object class.
 */
export abstract class PageObject<T
	extends IPageObjectRenderProps<IAbstractPageObject<IPageObjectTypes, IPageObjectProperties<ITransform2D>>>>
	extends DraggableWidget<T> {
	/**
	 * Image UUID to prevent image overlap
	 */
	imageFillUUID: string;

	/**
	 * Flag to represents whether escape key is press while dragging
	 */
	private isEscapeKeyPress: boolean | undefined = false;

	/** Timer for MultiSelect */
	private multiSelectTimer: number = 0;

	/** Variable for whether or not the shape was multiSelected onMouseDown */
	private didMultiSelect: boolean = false;

	/** Position for pageShape */
	private pageShapePosition: IPosition = {
		x: 0,
		y: 0,
	};

	/**
	 * Guides on how to scale the graphics as per view box.
	 * @returns {string}
	 */
	protected getPreserveAspectRatio(): string {
		return "none";
	}

	/**
	 * Get view box for the Page object, child class can override this method to scale view box as per requirements.
	 * @returns {height: number, left: number, top: number, width: number} View box.
	 */
	protected getViewBox(): { height: number; left: number; top: number; width: number } {
		return {
			height: this.properties.pageObject.spPr.xfrm.ext.cy,
			left: 0,
			top: 0,
			width: this.properties.pageObject.spPr.xfrm.ext.cx,
		};
	}

	/**
	 * Abstract template method to render object based on its kind. Either shape or group shape or line or picture
	 * @returns {DNode} Virtual dom for page object kind
	 */
	protected abstract renderObject(): DNode;

	/**
	 * Render defs for marker, override this method in Shape
	 */
	protected renderDefs(): DNode {
		return null;
	};

	/** Get image fill defs. Shape module will override this method */
	protected getImageFillDefs(): DNode {
		return null;
	}

	/**
	 * Abstract template method to render shape text body, if available.
	 * This method, essentially applicable to shapes only and shape's renderObject method can take care of it.
	 * However, we need to render it later in sequence, hence the dedicated template method
	 */
	protected abstract renderIfShapeTextBody(): DNode;

	/**
	 * Pointer down event for shape
	 * @param event {PointerEvent}
	 */
	protected _onShapePointerDown(event: PointerEvent) {
		if (getPanningMode()) {
			return false;
		}

		// on shift key press allow multiple shape select
		let isMultiSelect: boolean = allowMultiShapeSelection(event);

		// set didMultiSelect to false initially
		this.didMultiSelect = false;

		if (stateConstants().isFormatPainterActive && this.properties.applyFormatPainter) {
			this.properties.applyFormatPainter(this.properties.pageObject.shapeId);
			// if format painter is on, does not allow multiple object selection
			isMultiSelect = false;
		}
		// If the group is not currently selected, prevent the first pointerUp event from drilling into the group.
		// We only want to drill in to groups after single-click, non-dragging pointer up.
		if (this.properties.partOfGroup) {
			const anywindow = window as any;
			anywindow.blockNextPointerUp = !this.properties.parentGroupIsSelected;
		}
		// If we are not multiselecting, instantly select / deselect the object onMouseDown
		if (this.properties.selectObject && !isMultiSelect) {
			this.properties.selectObject(this.properties.key as number, false, true);
			// otherwise, let's start a timer and grab the position so that multiselect can work properly onDrag
		} else if (this.properties.selectObject && isMultiSelect) {
			// if the shape or group is not selected onMouseDown, select it
			if (!this.properties.isSelected && !this.properties.parentGroupIsSelected) {
				this.properties.selectObject(this.properties.key as number, true, true);
				// set didMultiSelect to true because it was multiselected onMouseDown
				this.didMultiSelect = true;
			}
			this.multiSelectTimer = Date.now();
			this.pageShapePosition = this.properties.pageObject.spPr.xfrm.off;
		}
	}

	/**
	 * Pointer up event for shape
	 * @param event {PointerEvent}
	 */
	protected _onShapePointerUp(event: PointerEvent) {
		const anywindow = window as any;
		const pageShape = this.properties.pageObject as IShape;
		let placeholder = pageShape.imagePlaceholder;
		const isMultiSelect: boolean = allowMultiShapeSelection(event);
		if (pageShape.isPicture) {
			placeholder = (pageShape as any as IPicture).isPictureFromPlaceholder;
		} else if (pageShape.attributeKey) {
			placeholder = true;
		}
		if (placeholder && !pageShape.spPr.locked && !this.properties.absoluteLock) {
			anywindow.placeholderTarget = pageShape;

			// Flag is not unset successfully on clicks outside of shape.
			// It should never take more than 1 second for this flag to be read
			// so we can safely unset it afterward.
			setTimeout(() => {
				anywindow.placeholderTarget = null;
			}, 1000);
		} else {
			anywindow.placeholderTarget = null;
		}

		if (getPanningMode()) {
			return false;
		}

		// Using the timer we started onMouseDown combined with whether or not we're multiselecting, multiselect or
		// multi-deselect the shape if it hasn't moved, the timer has not exceeded 500 milliseconds, and it was not multiselected onMouseDown
		if (this.properties.selectObject && isMultiSelect && (Date.now() - this.multiSelectTimer) < 500 &&
			pageShape.spPr.xfrm.off.x === this.pageShapePosition.x &&
			pageShape.spPr.xfrm.off.y === this.pageShapePosition.y &&
			!this.didMultiSelect) {
			this.properties.selectObject(this.properties.key as number, true, true);
		}

		// If we have clicked on a single group element and we had the group selected already when the click
		// started, drill into and select the group item.
		if (this.properties.partOfGroup && this.properties.parentGroupIsSelected) {
			if (!anywindow.blockNextPointerUp && !isMultiSelect) {
				this.properties.selectObject(this.properties.key as number, false, true);
			} else {
				anywindow.blockNextPointerUp = false;
			}
		}
	}

	/**
	 * Mouse Double click event for the page object.
	 * @param event {MouseEvent}
	 */
	protected _onShapeDblClick(event: MouseEvent) {
		if (this.properties.partOfGroup && !this.properties.isSelected) {
			this.properties.selectObject(this.properties.key as number, false, false);
		} else if (this.properties.editPageObject) {
			this.properties.selectObjectById(this.properties.pageObject.shapeId, false, false, true);
			this.properties.editPageObject(false, false, this.properties.pageObject.shapeId);
		} else if (this.properties.partOfGroup) {
			const isMultiSelect: boolean = allowMultiShapeSelection(event);
			this.properties.selectObject(this.properties.key as number, isMultiSelect, false);
		}
	}

	/**
	 * Pointer drag event for shape
	 * @param drag {DragResults}
	 */
	protected onDrag(drag: IDragResults, isComplete: boolean) {
		if (this.properties.translateObjects && !getPanningMode()) {
			const anywindow = window as any;
			if (!anywindow.blockNextPointerUp && drag.start && drag.start.offset && drag.current) {
				const offX = Math.abs(drag.start.offset.x - drag.current.x);
				const offY = Math.abs(drag.start.offset.y - drag.current.y);
				if (offX > 2 || offY > 2) {
					anywindow.blockNextPointerUp = true;
				}
			}
			this.isEscapeKeyPress = cancelCurrentOperation(drag.keyData);
			if (!isComplete) {
				this.properties.translateObjects(drag, true);
			} else {
				this.properties.translateObjects(drag, false, this.isEscapeKeyPress);
			}
		}
	}

	/**
	 * Render component.
	 * This is common to shape and pictures.
	 * Note: Group shape will override this method to generate its own component
	 *
	 * @returns DNode for shape or picture component
	 */
	protected _renderComponent(): DNode | DNode[] {
		let { rot } = this.properties.pageObject.spPr.xfrm;
		const { flipH } = this.properties.pageObject.spPr.xfrm;
		let { cx, cy } = this.properties.pageObject.spPr.xfrm.ext;
		this.imageFillUUID = generateUUID();
		cx = getMaxDimension(cx);
		cy = getMaxDimension(cy);
		let scale = "";
		const preserveAspectRatio = this.getPreserveAspectRatio();

		if (flipH) {
			scale = "scale(-1, 1)";
		}
		rot = rot || ANGLE_ZERO;
		const transformOrigin = this.properties.transformOrigin ?
			`${this.properties.transformOrigin.x}px ${this.properties.transformOrigin.y}px` : "center";
		return (
			<div
				key={`pageObject_matrix_${this.properties.key}`}
				styles={{
					height: `${cy}px`,
					overflow: "visible",
					pointerEvents: "none",
					transform: `rotate(${rot}deg) ${scale}`,
					transformOrigin: `${transformOrigin}`,
					width: `${cx}px`,
				}
				}
			>
				<svg
					height={`${Math.ceil(cy) + ADDITIONAL_DIMENSION_FACTOR}px`}
					id={`pageObject_${this.properties.pageObject.shapeId}`}
					preserveAspectRatio={`${preserveAspectRatio}`}
					/* Reference: C8-6740 - to avoid caterpillar like effect while moving shape,
					* additional dimension factor is being added to SVG element's width and height.
					* Assigning -2px to top & left and 2px to padding top & padding left attributes
					* for adjusting the position of the SVG element so that shape will be rendered
					* at correct position.
					*/
					styles={{
						overflow: "visible",
						top: "-2px",
						left: "-2px",
						position: "absolute",
						paddingTop: "2px",
						paddingLeft: "2px",
					}}
					width={`${Math.ceil(cx) + ADDITIONAL_DIMENSION_FACTOR}px`}
					xmlns="http://www.w3.org/2000/svg"
				>
					{this.renderDefs()}
					{this.getImageFillDefs()}
					{this.renderObject()}
				</svg>
				{this.renderIfShapeTextBody()}
			</div>
		);
	}

	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		const key = `pageObject_${this.properties.pageObject.shapeId}`;
		// prevent listening for drag on temporary shapes (shapes shown while drawing)
		if (this.properties.key !== TEMP_SHAPE) {
			this.requestDragMeta(key);
		}
		let top: string = "";
		let left: string = "";
		top = `${this.properties.pageObject.spPr.xfrm.off.y}px`;
		left = `${this.properties.pageObject.spPr.xfrm.off.x}px`;

		return (
			<div
				key={`${key}`}
				styles={
					{
						display: "inline-block",
						left: left,
						overflow: "visible",
						pointerEvents: "none",
						position: "absolute",
						top: top,
						zIndex: `${1+(this.properties.key as number)}`,
					}}
			>
				{this._renderComponent()}
			</div>
		);
	}
}

export default PageObject;
